/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { WorkspaceDocVersion, UpdateWorkspaceVersion, VersionComparisonResponse } from '@workspaces/models/versions';
import { WorkspaceVersionService } from '@workspaces/services/version.service';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

const generateMockVersions = (): WorkspaceDocVersion[] => {
  const count = Math.floor(Math.random() * 21) + 10; // 10 to 30
  const versions: WorkspaceDocVersion[] = [];
  const baseDate = new Date();

  const aliases = [
    'Final Approved Version (Published)',
    'Minor Content Correction',
    'Draft 3 Review',
    'First Reviewer Feedback Incorporated',
    'Initial Document Creation',
    null,
    null,
    null
  ];
  const tagsList = [['final', 'published'], ['edit'], ['draft', 'review'], ['feedback'], ['initial'], null];

  for (let i = 0; i < count; i++) {
    const isRestored = Math.random() > 0.8;
    const date = new Date(baseDate);
    date.setHours(date.getHours() - i * (Math.random() * 10 + 1));

    versions.push({
      version_id: `v-${Math.random().toString(36).substring(2, 7)}`,
      alias: aliases[Math.floor(Math.random() * aliases.length)],
      created_at: date.toISOString(),
      tags: tagsList[Math.floor(Math.random() * tagsList.length)],
      restored_version_id: isRestored ? `v-${Math.random().toString(36).substring(2, 7)}` : '',
      restored_version_alias: isRestored ? 'Restored Version' : null,
      is_latest: false,
      size: 0
    });
  }
  return versions;
};

const mockVersions: WorkspaceDocVersion[] = generateMockVersions();

@Injectable()
export class MockWorkspaceVersionService extends WorkspaceVersionService implements WorkspaceVersionService {
  private mockDelay = 500;
  private useVersion1 = true;
  public override getVersion(workspaceId: string) {
    if (workspaceId === 'error-id') {
      return throwError(() => new Error('Failed to retrieve versions.')).pipe(delay(this.mockDelay));
    }

    return of(mockVersions).pipe(delay(this.mockDelay));
  }

  public override restoreVersion(_workspaceId: string): Observable<void> {
    const restoredVersion: WorkspaceDocVersion = {
      version_id: `v-new-${Date.now()}`,
      alias: `Restored: ${new Date().toLocaleTimeString()}`,
      created_at: new Date().toISOString(),
      tags: ['restored', 'current'],
      restored_version_id: mockVersions[0].version_id,
      restored_version_alias: mockVersions[0].alias,
      is_latest: false,
      size: 0
    };
    mockVersions.unshift(restoredVersion);

    return of(undefined as void).pipe(delay(this.mockDelay));
  }

  public override updateVersion(
    workspaceId: string,
    versionId: string,
    updateData: UpdateWorkspaceVersion
  ): Observable<void> {
    const latestVersion = mockVersions[0];

    if (latestVersion) {
      latestVersion.alias = updateData.alias;
      latestVersion.tags = updateData.tags;
    }

    return of(undefined as void).pipe(delay(this.mockDelay));
  }
  public override compareVersions(
    workspaceId: string,
    version1: string,
    version2: string
  ): Observable<VersionComparisonResponse> {
    this.useVersion1 = !this.useVersion1;
    return of({
      workspace_id: workspaceId,
      version1: '',
      version2: '',
      diff_html: Array(10)
        .fill(this.useVersion1 ? ExampleDiffHtml1 : ExampleDiffHtml2)
        .join('')
    } satisfies VersionComparisonResponse).pipe(delay(this.mockDelay));
  }
}
export const ExampleDiffHtml1 = `<div class="diff-container">

  <h1>Project Overview</h1>
  <p>
    The purpose of this document is to outline the core objectives of the system.
    The system <del>was originally intended</del>
    <ins>is now redesigned</ins> to support large-scale distributed workloads.
    It aims to deliver <del>moderate</del><ins>high</ins> reliability and improved
    performance under heavy usage.
  </p>

  <h2>Key Features</h2>
  <ul>
    <li>
      User authentication with OAuth2
      <del>and limited session control</del>
      <ins>including multi-device session management</ins>
    </li>
    <li>
      Realtime analytics dashboard with
      <del>basic</del> <ins>advanced</ins> visualization options
    </li>
    <li>
      <del>Local-only caching</del>
      <ins>Distributed caching with automatic failover</ins>
    </li>
    <li>
      Cloud synchronization module
      <ins>with conflict resolution logic</ins>
    </li>
  </ul>

  <h2>System Architecture</h2>
  <p>
    The original design emphasized monolithic deployment. However, after several
    performance reviews, the team introduced a microservice-oriented architecture,
    enabling independent scaling of critical components.
  </p>

  <p>
    Communication between services is handled through a
    <del>REST-only protocol</del>
    <ins>combination of REST and event-based messaging</ins>,
    providing more flexibility when dealing with asynchronous operations.
  </p>

  <h3>Service Diagram Notes</h3>
  <p>
    The diagram below highlights recent modifications:
  </p>

  <ul>
    <li>
      <del>Removed direct database access from the reporting module</del>
      <ins>Added a query proxy service for secure data retrieval</ins>
    </li>
    <li>
      Enhanced load balancer to support <ins>zero-downtime deployments</ins>
    </li>
    <li>
      <del>Deprecated XML message format</del>
      <ins>Replaced with standardized JSON schemas</ins>
    </li>
  </ul>

  <h2>Configuration Values</h2>
  <table border="1" cellpadding="5" cellspacing="0">
    <thead>
      <tr>
        <th>Setting</th>
        <th>Previous Value</th>
        <th>Updated Value</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>maxWorkers</td>
        <td><del>4</del></td>
        <td><ins>16</ins></td>
      </tr>
      <tr>
        <td>cacheTTL</td>
        <td><del>300</del></td>
        <td><ins>900</ins></td>
      </tr>
      <tr>
        <td>loggingLevel</td>
        <td><del>info</del></td>
        <td><ins>verbose</ins></td>
      </tr>
      <tr>
        <td>apiTimeout</td>
        <td><del>1500ms</del></td>
        <td><ins>2500ms</ins></td>
      </tr>
      <tr>
        <td>syncMode</td>
        <td><del>manual</del></td>
        <td><ins>automatic</ins></td>
      </tr>
    </tbody>
  </table>

  <h2>Module-Specific Notes</h2>

  <h3>1. Authentication Module</h3>
  <p>
    The login flow has been reworked. The previous model relied on a simple username and
    password combination.
    <del>The password recovery flow was incomplete.</del>
    <ins>A complete recovery workflow, including email and SMS verification, has been added.</ins>
  </p>

  <h3>2. File Sync Module</h3>
  <p>
    File synchronization previously used a polling strategy checking for new files every
    60 seconds.
    <del>Polling is now discouraged due to performance issues.</del>
    <ins>An event-driven notifier has replaced the polling mechanism.</ins>
  </p>

  <pre>
  {
    "syncInterval": "<del>60000</del><ins>0  // replaced by event-driven</ins>",
    "maxFileSize": "50MB",
    "allowPartialUploads": true
  }
  </pre>

  <h3>3. Analytics Module</h3>
  <p>
    Earlier versions of the analytics module generated reports using synchronous parsing logic.
    <del>This caused slowdowns when processing large datasets.</del>
    <ins>The system now performs parsing asynchronously using worker threads.</ins>
  </p>

  <p>
    Additional enhancements include:
  </p>

  <ul>
    <li>
      <del>Limited chart options</del>
      <ins>Full range of dynamic chart types</ins>
    </li>
    <li>
      <ins>Added predictive analytics model integration</ins>
    </li>
    <li>
      <del>No export capability</del>
      <ins>Export to CSV, JSON, and PDF</ins>
    </li>
  </ul>

  <h2>Conclusion</h2>
  <p>
    The system has undergone substantial improvements. Many legacy components were removed
    or rewritten to support modern workloads. The deployment pipeline, previously limited,
    <del>required manual effort</del>
    <ins>is now fully automated through CI/CD workflows</ins>, resulting in faster release cycles
    and reduced error rates.
  </p>

  <p>
    Future work includes:
    <ins>real-time alerting, automatic scaling policies, and multi-region redundancy</ins>.
  </p>

</div>
`;

export const ExampleDiffHtml2 = `
<div class="diff-container">
  <h2><del>Draft</del><ins>Final</ins> Report: Project <del>Alpha</del><ins>Zeta</ins> Summary</h2>
  
  <p class="section-intro">
    This document <del>serves as</del><ins>is the comprehensive</ins> summary of the findings <del>from the initial phase</del><ins>and conclusions</ins> of Project Zeta, spanning Q1 and Q2.
  </p>

  <hr>

  <h3>1. Methodology <del>Overview</del></h3>
  <p>
    The research methodology <ins>adopted for this project</ins> was primarily <del>qualitative</del><ins>a mixed-methods approach</ins>, combining:
    <ul>
      <li><del>Initial interviews (n=15)</del></li>
      <li><ins>Structured interviews with key stakeholders (n=25)</ins></li>
      <li>Quantitative data analysis of <del>server logs</del><ins>transactional records</ins>.</li>
    </ul>
    A significant revision was made <del>on March 15th</del><ins>during the mid-point review</ins> to account for the unexpected volatility in the <del>supply chain metrics</del><ins>external market indicators</ins>.
  </p>
  
  <hr>

  <h3>2. Key Findings and Analysis</h3>
  
  <h4>2.1. <ins>Customer</ins> Engagement Metrics</h4>
  <p>
    <del>Customer engagement remained flat throughout the first quarter</del>. <ins>Contrary to initial projections, customer engagement saw a 12% increase in Q2</ins>. The primary drivers for this change were identified as:
    <ol>
      <li>The successful implementation of the new <del>notification system</del><ins>personalized outreach campaign</ins>.</li>
      <li>A 5% reduction in average page load time, <del>as previously reported</del><ins>attributed to server optimization efforts</ins>.</li>
    </ol>
    The outlier data point from <del>April 3rd</del><ins>the first week of May</ins> was deemed <del>inconclusive</del><ins>an anomaly</ins> and was excluded from the final regression model.
  </p>

  <h4>2.2. Financial <del>Implications</del><ins>Performance</ins></h4>
  <p>
    The total project expenditure <ins>exceeded the initial budget by 7%</ins>, primarily due to <del>unforeseen licensing fees</del><ins>accelerated procurement of critical infrastructure</ins>. 
    The return on investment (ROI) projection remains <del>optimistic, but subject to revision</del><ins>strong, estimated at 1.4:1</ins>.
  </p>

  <hr>

  <h3>3. Recommendations <del>for Next Steps</del></h3>
  <p>
    We strongly recommend <del>exploring</del><ins>the immediate initiation of</ins> Phase 2, focusing specifically on:
    <ul>
      <li><del>Scaling the current solution across all regional offices.</del></li>
      <li><ins>Conducting a deep-dive feasibility study on global expansion.</ins></li>
      <li><del>Hiring two additional data analysts.</del><ins>Allocating existing engineering resources to system hardening.</ins></li>
    </ul>
    The project team <ins>is prepared to present</ins> these findings to the steering committee <del>next week</del>.
  </p>
</div>
`;
